import React, { useState, useEffect } from 'react';
import '../../../pricing-simulator/components/list-price-simulation-table/list-price-simulation.scss';
import { TableState } from '@tanstack/table-core';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import CollapsibleTable from 'src/components/collapsible-table/collapsible-table';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import { makeDataForTable } from 'src/components/collapsible-table/table-data-prep';
import { AccordionDetails } from 'src/components/expandable-accordion/accordion-details';
import { Accordion } from 'src/components/expandable-accordion/accordion-style';
import {
  getMultiyearEstimateData,
  handleMultiyearToggleChange
} from '../../store/simulation-summary-slice';
import TableSkeletonContainer from '../../../pricing-simulator/components/simulation-table-container/table-skeleton';
import { TableComponent } from '../../../pricing-simulator/types/generic-table.model';
import getOutputTableColumn from '../../../common/features/table/components/output-column-prep';
import {
  handleInputLevelToggleChange,
  setTableMetaData
} from '../../../common/features/table/store/table-slice';

export default function MultiyearEstimateComponent({
  moduleId,
  scenarioId,
  sourceId,
  inputsDisabled,
  mode = 'summary'
}: {
  sourceId: number;
  moduleId: number;
  scenarioId: number;
  inputsDisabled: boolean;
  mode?: 'summary' | 'comparison';
}) {
  const appDispatch = useAppDispatch();
  const [multiyearToggleValue, setMultiyearToggleValue] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState<string | false>('panel1');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const {
    fetchingMultiyearEstimateData,
    multiyearEstimateData,
    multiyearToggle
  } = useAppSelector((state) => state.simulationSummary);
  const isTargetEnabled = useAppSelector((state) => state.scenarioComparison.isTargetEnabled);
  const currencyFlag = mode === 'comparison' ? isTargetEnabled : undefined;

  const { outputToggleOptions } = useAppSelector(
    (state: { table: any }) => state.table
  );

  useEffect(() => {
    appDispatch(setTableMetaData({ tableData: multiyearEstimateData }));
  }, [multiyearEstimateData]);

  const {
    tableMetadaData,
    inputCols,
    resultCols,
    outputColumnVisibilityState
  } = useAppSelector((state) => state.table);

  const tableState: Partial<TableState> = {
    columnVisibility: outputColumnVisibilityState
  };

  useEffect(() => {
    if (
      tableMetadaData.row &&
      tableMetadaData.row.length &&
      multiyearEstimateData.inputCols.length > 0
    ) {
      setColumns([
        ...getTableColumnData(
          inputCols,
          TableComponent.MULTIYEARESTIMATE,
          multiyearEstimateData.header,
          multiyearEstimateData.footer.row,
          inputsDisabled
        ),
        ...getOutputTableColumn(
          resultCols,
          multiyearEstimateData.output_toggle,
          multiyearEstimateData.footer.row,
          {},
          TableComponent.MULTIYEARESTIMATE
        )
      ]);
      setRows(
        makeDataForTable(tableMetadaData.row).sort((p1, p2) =>
          p2.cell < p1.cell ? 1 : p2.cell > p1.cell ? -1 : 0
        )
      );
    }
  }, [tableMetadaData, multiyearEstimateData, inputCols, resultCols]);

  useEffect(() => {
    if (sourceId && moduleId && scenarioId) {
      const multiyearPayload = {
        filter_inputs: {},
        row: {
          data: [],
          inputs: []
        },
        ...(mode === 'comparison' && isTargetEnabled != undefined && { currency_toggle: isTargetEnabled })
      };
      // get multi year data on load
      appDispatch(
        getMultiyearEstimateData({
          dataInput: multiyearPayload,
          sourceId,
          moduleId,
          scenarioId
        })
      ).then((res) => {
        setTimeout(() => {
          if (
            multiyearEstimateData?.output_toggle?.data &&
            outputToggleOptions.length == 2
          ) {
            appDispatch(handleInputLevelToggleChange({ toggledView: 'abs' }));
          } else {
            appDispatch(handleInputLevelToggleChange({ toggledView: 'perc' }));
          }
        }, 300);
      });
    }
  }, [moduleId, currencyFlag]);

  // useEffect(() => {
  //   if (multiyearToggle && multiyearToggle === 'perc') {
  //     setMultiyearToggleValue(false);
  //   } else {
  //     setMultiyearToggleValue(true);
  //   }
  // }, [multiyearToggle]);

  const handleAccordionOnChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpandedPanel(newExpanded ? panel : false);
    };

  const toggleUpliftValueSwitch = (e) => {
    setMultiyearToggleValue(e.target.checked);
    if (e.target.checked) {
      appDispatch(handleMultiyearToggleChange({ multiyearToggle: 'abs' }));
    } else {
      appDispatch(handleMultiyearToggleChange({ multiyearToggle: 'perc' }));
    }
  };

  return (
    <div style={{ overflowY: 'auto' }}>
      {fetchingMultiyearEstimateData ? (
        <TableSkeletonContainer rowLength={10} headerLength={12} />
      ) : (
        <Accordion
          expanded={expandedPanel === 'panel1'}
          onChange={handleAccordionOnChange('panel1')}
        >
          <AccordionDetails style={{ padding: '0px' }}>
            <div style={{ margin: '0px 10px' }}>
              <CollapsibleTable
                data={rows}
                setHeightExplicitly={false}
                showFooterLabel={false}
                columns={columns}
                tableState={tableState}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
}